/* Lifted — shared country/currency switcher init.
 *
 * Wires EVERY [data-locale-picker] on the page: the nav desktop <details>
 * pill, the mobile burger-drawer picker, and the footer pill. Handles:
 *   - ISO text -> flag emoji on every flag span
 *   - open/close for the button+panel variant (the details variant is native)
 *   - ESC + outside-click close
 *   - submission via a manual fetch POST to the form's action + reload
 *     (bypasses an iOS Safari implicit-submitter quirk; the desktop/footer
 *     type="submit" buttons are the no-JS fallback)
 *   - the country search filter
 *
 * Lifted out of sections/lifted-header.liquid (where it ran inside an
 * immediately-invoked block, so it only ever saw pickers already parsed —
 * i.e. NOT the footer). Running it at parse-complete here means the footer
 * instance gets wired too. The per-picker guard makes a double-load a no-op.
 */
(function () {
  const isoToFlag = (iso) => {
    if (!iso || iso.length !== 2) return '';
    const A = 0x1F1E6;
    return String.fromCodePoint(A + iso.charCodeAt(0) - 65, A + iso.charCodeAt(1) - 65);
  };

  function initLocalePickers() {
    document.querySelectorAll('[data-locale-picker]').forEach(picker => {
      if (picker.__localeBound) return; // never double-bind
      picker.__localeBound = true;

      // Replace ISO text with flag emoji on every flag span
      picker.querySelectorAll('[data-locale-flag], .lifted-locale__flag').forEach(el => {
        const txt = el.textContent.trim();
        if (txt.length === 2 && /^[A-Z]+$/.test(txt)) {
          el.textContent = isoToFlag(txt);
        }
      });

      const trigger = picker.querySelector('[data-locale-trigger]');
      const panel = picker.querySelector('[data-locale-panel]');
      const form = picker.querySelector('form');

      function open() {
        if (!panel) return;
        panel.removeAttribute('hidden');
        trigger?.setAttribute('aria-expanded', 'true');
      }
      function close() {
        if (!panel) return;
        panel.setAttribute('hidden', '');
        trigger?.setAttribute('aria-expanded', 'false');
      }
      trigger?.addEventListener('click', (e) => {
        e.stopPropagation();
        panel?.hasAttribute('hidden') ? open() : close();
      });
      document.addEventListener('click', (e) => {
        if (!picker.contains(e.target)) close();
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

      // JS-driven submission via fetch (see header note): build the POST body
      // manually, force country_code to the tapped iso, return to this page,
      // then reload so Shopify's new country/currency cookie takes effect.
      picker.addEventListener('click', async (e) => {
        const opt = e.target.closest('[data-locale-opt]');
        if (!opt || !form) return;
        e.preventDefault();
        e.stopPropagation();
        const iso = opt.dataset.iso;
        if (!iso) return;
        const params = new URLSearchParams();
        new FormData(form).forEach((v, k) => {
          if (k === 'country_code') return;
          params.append(k, v);
        });
        params.append('country_code', iso);
        if (!params.has('return_to')) {
          params.append('return_to', window.location.pathname + window.location.search);
        }
        picker.querySelectorAll('[data-locale-opt]').forEach(b => {
          b.classList.toggle('is-active', b === opt);
        });
        try {
          await fetch(form.action || '/localization', {
            method: 'POST',
            body: params,
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              'Accept': 'text/html,application/xhtml+xml'
            },
            credentials: 'same-origin',
            redirect: 'follow'
          });
        } catch (err) {
          console.error('[locale] /localization POST failed:', err);
        }
        window.location.reload();
      });

      // Search filter (present on the desktop + footer variants)
      const searchInput = picker.querySelector('[data-locale-search]');
      searchInput?.addEventListener('input', () => {
        const q = searchInput.value.trim().toLowerCase();
        picker.querySelectorAll('[data-locale-opt]').forEach(opt => {
          const name = (opt.dataset.name || '').toLowerCase();
          const cur = (opt.dataset.cur || '').toLowerCase();
          const iso = (opt.dataset.iso || '').toLowerCase();
          const match = !q || name.includes(q) || cur.includes(q) || iso.includes(q);
          if (match) opt.removeAttribute('hidden'); else opt.setAttribute('hidden', '');
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLocalePickers);
  } else {
    initLocalePickers();
  }
})();
